import { useState } from 'react'
import { Link } from 'react-router-dom'

import { useAction } from '../components/toast'
import { Badge, Card, Loaded, Notice, Page, ReasonDialog, Table } from '../components/ui'
import { get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { dateTime, relative } from '../lib/format'
import { useResource } from '../lib/useResource'

type CareLink = {
  id: string
  patient_id: string
  clinician_id: string
  scopes: string[]
  granted_at: string | null
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
  break_glass_reason: string | null
}

function state(link: CareLink): { label: string; tone?: 'green' | 'orange' | 'alert' } {
  if (link.revoked_at) return { label: 'Revoked' }
  if (link.break_glass_reason) return { label: 'Break-glass', tone: 'alert' }
  if (link.expires_at && new Date(link.expires_at) < new Date())
    return { label: 'Expired', tone: 'orange' }
  return { label: 'Active', tone: 'green' }
}

export function CareLinks() {
  const { can } = useAuth()
  const run = useAction()
  const [revoking, setRevoking] = useState<CareLink | null>(null)
  const links = useResource((signal) => get<CareLink[]>('/v1/admin/care-links', undefined, signal))

  const breakGlass = (links.data ?? []).filter((l) => l.break_glass_reason && !l.revoked_at)

  return (
    <Page
      title="Care links"
      subtitle="Time-boxed, patient-granted clinician access. A link is the only way a clinician sees a patient they are not otherwise connected to."
    >
      {breakGlass.length > 0 && (
        <Notice tone="phi">
          <strong>{breakGlass.length} active break-glass access{breakGlass.length === 1 ? '' : 'es'}.</strong>{' '}
          Break-glass is access the patient never granted, taken under a stated clinical urgency.
          Each one needs a human to read the reason and decide whether it stands.
        </Notice>
      )}

      <Card flush>
        <Loaded resource={links}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(row) => row.id}
              empty="No clinician has been granted access to a patient record."
              columns={[
                {
                  key: 'state',
                  header: 'State',
                  render: (row) => {
                    const s = state(row)
                    return <Badge tone={s.tone}>{s.label}</Badge>
                  },
                },
                {
                  key: 'patient',
                  header: 'Patient',
                  strong: true,
                  render: (row) => (
                    <Link className="mono" to={`/users/${row.patient_id}`}>
                      {row.patient_id.slice(0, 8)}…
                    </Link>
                  ),
                },
                {
                  key: 'clinician',
                  header: 'Clinician',
                  render: (row) => (
                    <Link className="mono" to={`/users/${row.clinician_id}`}>
                      {row.clinician_id.slice(0, 8)}…
                    </Link>
                  ),
                },
                {
                  key: 'scopes',
                  header: 'Scope',
                  render: (row) => (
                    <span className="row row--wrap" style={{ gap: 4 }}>
                      {row.scopes.length === 0 ? (
                        <span className="dim">—</span>
                      ) : (
                        row.scopes.map((s) => <Badge key={s}>{s}</Badge>)
                      )}
                    </span>
                  ),
                },
                { key: 'granted', header: 'Granted', render: (row) => <span className="dim">{dateTime(row.granted_at)}</span> },
                { key: 'expires', header: 'Expires', render: (row) => <span className="dim">{relative(row.expires_at)}</span> },
                { key: 'used', header: 'Last used', render: (row) => <span className="dim">{relative(row.last_used_at)}</span> },
                {
                  key: 'reason',
                  header: 'Break-glass reason',
                  render: (row) =>
                    row.break_glass_reason ? (
                      <span style={{ color: 'var(--alert-pink)' }}>{row.break_glass_reason}</span>
                    ) : (
                      <span className="dim">—</span>
                    ),
                },
                {
                  key: 'action',
                  header: '',
                  render: (row) =>
                    row.revoked_at || !can(P.userWriteAny) ? null : (
                      <button
                        className="btn btn--sm btn--danger"
                        onClick={(event) => {
                          event.stopPropagation()
                          setRevoking(row)
                        }}
                      >
                        Revoke
                      </button>
                    ),
                },
              ]}
            />
          )}
        </Loaded>
      </Card>

      {revoking && (
        <ReasonDialog
          title="Revoke this care link"
          description="The clinician loses access immediately. If they are mid-consult, say so here — the reason is what a later review reads."
          confirmLabel="Revoke access"
          danger
          onClose={() => setRevoking(null)}
          onConfirm={(reason) =>
            run(
              () => post(`/v1/admin/care-links/${revoking.id}/revoke`, { reason }),
              'Care link revoked.',
              links.reload,
            )
          }
        />
      )}
    </Page>
  )
}
